/*
 * TSOGN ERA MEDIUM SYSTEM
 *
 * Each era gets a writing-medium profile that defines:
 *   - surface: the physical "paper" — stone, papyrus, vellum, parchment, paper, newsprint, screen
 *   - ink: foreground color of the rendered text
 *   - background: the surface tone behind the text
 *   - shadowInk: subtle ink-shadow to suggest depth (chisel, embossed type, etc)
 *   - edgeStyle: how the medium frames itself — chiseled, curled, deckled, gutter, scanlines
 *   - fontStack: era-appropriate typography (period-faithful where useful)
 *   - tool: { kind, baseFreq, decay, brightness } — drives the synthesized writing sound
 *
 * Methodology hold: medium reinforces era. NEVER sabotage readability.
 * If a treatment makes text harder to read, simplify the treatment, not the readability.
 *
 * Performance: CSS-only textures via SVG data URLs + repeating gradients.
 * No raster assets. 60fps target on low-end Android.
 */

export const ERA_MEDIA = {
  // Era 0 — Deep Prehistoric (cave wall, ochre, charcoal)
  era0: {
    surface: 'cave-wall',
    ink: '#E8B585',
    inkShadow: '0 0 1px rgba(232,181,133,0.4)',
    background: '#1A120A',
    backgroundOverlay: 'radial-gradient(circle at 30% 20%, rgba(255,180,90,0.04), transparent 50%), radial-gradient(circle at 70% 80%, rgba(255,180,90,0.03), transparent 60%)',
    grain: 'cave',
    edgeStyle: 'rough-stone',
    fontStack: "'Cinzel Decorative', 'Trajan Pro', serif",
    fontWeight: 500,
    fontSize: '1.05rem',
    letterSpacing: '0.04em',
    label: 'CAVE WALL · OCHRE',
    tool: { kind: 'cave', baseFreq: 90, decay: 0.18, brightness: 0.4, density: 18 },
  },

  // Era 1 — Ancient (stone tablet / clay cuneiform)
  era1: {
    surface: 'stone-tablet',
    ink: '#3A2E1C',
    inkShadow: '1px 1px 0 rgba(0,0,0,0.45), 0 0 1px rgba(255,235,200,0.15)',
    background: '#C9B186',
    backgroundOverlay: 'radial-gradient(ellipse at 50% 30%, rgba(255,240,210,0.18), transparent 60%), radial-gradient(ellipse at 30% 80%, rgba(60,40,20,0.18), transparent 50%)',
    grain: 'stone',
    edgeStyle: 'chiseled',
    fontStack: "'Cinzel Decorative', 'Trajan Pro', serif",
    fontWeight: 600,
    fontSize: '1.05rem',
    letterSpacing: '0.06em',
    label: 'STONE TABLET · CHISELED',
    tool: { kind: 'chisel', baseFreq: 180, decay: 0.12, brightness: 0.7, density: 14 },
  },

  // Era 2 — Classical (papyrus scroll)
  era2: {
    surface: 'papyrus',
    ink: '#3D2914',
    inkShadow: 'none',
    background: '#D9C194',
    backgroundOverlay: 'repeating-linear-gradient(90deg, rgba(180,150,100,0.05) 0, rgba(180,150,100,0.05) 1px, transparent 1px, transparent 4px), repeating-linear-gradient(0deg, rgba(180,150,100,0.04) 0, rgba(180,150,100,0.04) 1px, transparent 1px, transparent 6px)',
    grain: 'papyrus',
    edgeStyle: 'curled-scroll',
    fontStack: "'Playfair Display', 'Cinzel', serif",
    fontWeight: 500,
    fontSize: '1.05rem',
    letterSpacing: '0.02em',
    label: 'PAPYRUS · HAND-LETTERED',
    tool: { kind: 'reed', baseFreq: 1400, decay: 0.06, brightness: 0.55, density: 12 },
  },

  // Era 3 — Medieval (vellum parchment with drop cap)
  era3: {
    surface: 'vellum',
    ink: '#2D1810',
    inkShadow: 'none',
    background: '#E5D4B0',
    backgroundOverlay: 'radial-gradient(circle at 20% 30%, rgba(120,80,40,0.08), transparent 40%), radial-gradient(circle at 80% 70%, rgba(120,80,40,0.06), transparent 50%)',
    grain: 'vellum',
    edgeStyle: 'deckled',
    fontStack: "'Uncial Antiqua', 'EB Garamond', serif",
    fontWeight: 500,
    fontSize: '1.05rem',
    letterSpacing: '0.02em',
    dropCap: { color: '#B22222', accent: '#C8A951' },
    label: 'VELLUM · ILLUMINATED',
    tool: { kind: 'quill', baseFreq: 900, decay: 0.09, brightness: 0.5, density: 16 },
  },

  // Era 4 — Early Modern (printed page, woodcut border)
  era4: {
    surface: 'printed-page',
    ink: '#1A1A1A',
    inkShadow: 'none',
    background: '#EFE4D0',
    backgroundOverlay: 'radial-gradient(ellipse at 50% 50%, rgba(80,60,30,0.04), transparent 70%)',
    grain: 'paper',
    edgeStyle: 'aged',
    fontStack: "'EB Garamond', 'Garamond', serif",
    fontWeight: 500,
    fontSize: '1rem',
    letterSpacing: '0.01em',
    label: 'PRINTED PAGE · WOODCUT',
    tool: { kind: 'press', baseFreq: 280, decay: 0.05, brightness: 0.65, density: 10 },
  },

  // Era 5 — Industrial (typeset newspaper)
  era5: {
    surface: 'newsprint',
    ink: '#16110B',
    inkShadow: 'none',
    background: '#E8DDC0',
    backgroundOverlay: 'repeating-linear-gradient(0deg, rgba(80,60,30,0.025) 0, rgba(80,60,30,0.025) 1px, transparent 1px, transparent 3px)',
    grain: 'newsprint',
    edgeStyle: 'columned',
    fontStack: "'Old Standard TT', 'Times New Roman', serif",
    fontWeight: 500,
    fontSize: '0.97rem',
    letterSpacing: '0',
    label: 'BROADSHEET · TYPESET',
    tool: { kind: 'press-fast', baseFreq: 320, decay: 0.04, brightness: 0.7, density: 8 },
  },

  // Era 6 — Early 20th Century (typewriter)
  era6: {
    surface: 'typewritten',
    ink: '#1F1A14',
    inkShadow: '0.3px 0.3px 0 rgba(60,40,20,0.4)',
    background: '#F0E8D2',
    backgroundOverlay: 'repeating-linear-gradient(90deg, rgba(60,40,20,0.02) 0, rgba(60,40,20,0.02) 1px, transparent 1px, transparent 7px)',
    grain: 'paper',
    edgeStyle: 'platen-roll',
    fontStack: "'Special Elite', 'Courier New', monospace",
    fontWeight: 400,
    fontSize: '0.98rem',
    letterSpacing: '0.02em',
    label: 'TYPEWRITTEN · CARBON',
    tool: { kind: 'typewriter', baseFreq: 380, decay: 0.07, brightness: 0.85, density: 7, dingEvery: 70 },
  },

  // Era 7 — Broadcast Era (memo / mid-century print)
  era7: {
    surface: 'mimeograph',
    ink: '#1A2E50',
    inkShadow: 'none',
    background: '#E0E5EE',
    backgroundOverlay: 'repeating-linear-gradient(0deg, rgba(20,40,80,0.02) 0, rgba(20,40,80,0.02) 1px, transparent 1px, transparent 4px)',
    grain: 'paper',
    edgeStyle: 'memo',
    fontStack: "'DM Sans', 'Helvetica Neue', sans-serif",
    fontWeight: 500,
    fontSize: '0.97rem',
    letterSpacing: '0',
    label: 'MIMEOGRAPH · OFFICE',
    tool: { kind: 'mimeo', baseFreq: 240, decay: 0.06, brightness: 0.6, density: 9 },
  },

  // Era 8 — Late 20th / Computer terminal
  era8: {
    surface: 'crt-terminal',
    ink: '#7AFF7A',
    inkShadow: '0 0 6px rgba(122,255,122,0.35), 0 0 1px rgba(122,255,122,0.6)',
    background: '#040A06',
    backgroundOverlay: 'repeating-linear-gradient(0deg, rgba(0,255,100,0.045) 0, rgba(0,255,100,0.045) 1px, transparent 1px, transparent 3px)',
    grain: 'crt',
    edgeStyle: 'scanlines',
    fontStack: "'IBM Plex Mono', 'Courier New', monospace",
    fontWeight: 400,
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
    label: 'CRT TERMINAL · PHOSPHOR',
    tool: { kind: 'keyboard', baseFreq: 520, decay: 0.04, brightness: 0.9, density: 5 },
  },

  // Era 9 — Contemporary (clean web — platform default)
  era9: {
    surface: 'web',
    ink: '#FFFFFF',
    inkShadow: 'none',
    background: 'transparent',
    backgroundOverlay: 'none',
    grain: 'none',
    edgeStyle: 'clean',
    fontStack: "'Cormorant Garamond', 'Georgia', serif",
    fontWeight: 400,
    fontSize: '1rem',
    letterSpacing: '0',
    label: 'WEB · LIVE',
    tool: { kind: 'web', baseFreq: 2400, decay: 0.025, brightness: 1.0, density: 4 },
  },
};

export function getEraMedium(eraId) {
  return ERA_MEDIA[eraId] || ERA_MEDIA.era9;
}
